import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import { sanitizeUrl } from '@braintree/sanitize-url';
import styled from 'styled-components';

import { ColorRoleField } from '../fields/widgets/color-role-field';
import { createSpacingFields } from '../fields/spacing-fields';
import {
  BODY_TEXT_SIZES,
  FONT_ROLES,
  lookup,
  NO_SPACING,
  resolveTextColor,
  spacingToCss,
  TEXT_COLOR_ROLES,
} from '../fields/tokens';
import type { BodyTextSize, BoxSpacing, FontRole } from '../fields/tokens';

type TextAlign = 'center' | 'left' | 'right';

/** `hover` keeps the underline off until pointer focus, which reads as less noisy in body copy. */
const UNDERLINE_MODES = {
  always: 'underline',
  hover: 'none',
  never: 'none',
} as const;

type UnderlineMode = keyof typeof UNDERLINE_MODES;

export type LinkProps = {
  text: string;
  href: string;
  openInNewTab: 'no' | 'yes';
  size: BodyTextSize;
  font: FontRole;
  color: string;
  underline: UnderlineMode;
  align: TextAlign;
  padding: BoxSpacing;
};

const Wrapper = styled.div`
  text-align: var(--cms-link-align, left);
  padding: var(--cms-link-padding, 0px);
`;

/**
 * The hover underline is driven by a separate custom property rather than a `hover` prop
 * so the rule stays in CSS and does not need a React state round-trip.
 */
const StyledLink = styled.a`
  color: var(--cms-link-color, ${TEXT_COLOR_ROLES.link});
  font-family: var(--cms-link-font-family, ${FONT_ROLES.body});
  font-size: var(--cms-link-font-size, ${BODY_TEXT_SIZES.lg});
  text-decoration: var(--cms-link-text-decoration, underline);
  cursor: pointer;

  &:hover {
    text-decoration: var(--cms-link-hover-text-decoration, underline);
  }
`;

const SIZE_OPTIONS: { label: string; value: BodyTextSize }[] = [
  { label: 'Extra small', value: 'xs' },
  { label: 'Small', value: 'sm' },
  { label: 'Medium', value: 'md' },
  { label: 'Large', value: 'lg' },
  { label: 'Extra large', value: 'xl' },
  { label: '2X large', value: 'xxl' },
];

export const linkConfig: ComponentConfig<LinkProps> = {
  label: 'Link',
  ai: {
    instructions:
      'A text hyperlink. Use for inline navigation and secondary calls to action where a full button would be too heavy.',
  },
  fields: {
    text: { type: 'text', label: 'Text' },
    href: {
      type: 'text',
      label: 'Link URL',
      ai: { stream: false, instructions: 'Full URL or relative path for the link.' },
    },
    openInNewTab: {
      type: 'radio',
      label: 'Open in new tab',
      options: [
        { label: 'No', value: 'no' },
        { label: 'Yes', value: 'yes' },
      ],
      ai: { schema: { type: 'string', enum: ['no', 'yes'] } },
    },
    size: {
      type: 'select',
      label: 'Size',
      options: SIZE_OPTIONS,
      ai: { schema: { type: 'string', enum: SIZE_OPTIONS.map(({ value }) => value) } },
    },
    font: {
      type: 'radio',
      label: 'Font',
      options: [
        { label: 'Heading', value: 'heading' },
        { label: 'Body', value: 'body' },
      ],
      ai: { schema: { type: 'string', enum: ['heading', 'body'] } },
    },
    color: {
      type: 'custom',
      label: 'Color',
      render: ColorRoleField,
      ai: {
        schema: {
          type: 'string',
          description:
            "Either a theme color role ('link', 'text', 'secondary', 'muted', 'inverted') or a hex value. Prefer 'link' so it matches the merchant's other links.",
        },
      },
    },
    underline: {
      type: 'radio',
      label: 'Underline',
      options: [
        { label: 'Always', value: 'always' },
        { label: 'Hover', value: 'hover' },
        { label: 'Never', value: 'never' },
      ],
      ai: {
        schema: { type: 'string', enum: ['always', 'hover', 'never'] },
        instructions:
          "Prefer 'always' or 'hover'. 'never' removes the only non-color cue that the text is a link, which is an accessibility risk.",
      },
    },
    align: {
      type: 'radio',
      label: 'Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
      ai: { schema: { type: 'string', enum: ['left', 'center', 'right'] } },
    },
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: createSpacingFields(),
    },
  },
  defaultProps: {
    text: 'Learn more',
    href: '#',
    openInNewTab: 'no',
    size: 'lg',
    font: 'body',
    color: 'link',
    underline: 'always',
    align: 'left',
    padding: NO_SPACING,
  },
  render: ({
    text = 'Learn more',
    href = '#',
    openInNewTab = 'no',
    size = 'lg',
    font = 'body',
    color = 'link',
    underline = 'always',
    align = 'left',
    padding,
  }) => {
    const newTab = openInNewTab === 'yes';
    const resting = lookup(UNDERLINE_MODES, underline, UNDERLINE_MODES.always);
    // `hover` is the only mode where resting and hover states differ.
    const hovered = underline === 'never' ? 'none' : 'underline';

    return (
      <Wrapper
        style={
          {
            '--cms-link-align': align,
            '--cms-link-padding': spacingToCss(padding),
          } as React.CSSProperties
        }
      >
        <StyledLink
          href={sanitizeUrl(href)}
          target={newTab ? '_blank' : undefined}
          // Without noopener the destination gets a window.opener handle back into the storefront.
          rel={newTab ? 'noopener noreferrer' : undefined}
          style={
            {
              '--cms-link-color': resolveTextColor(color),
              '--cms-link-font-family': lookup(FONT_ROLES, font, FONT_ROLES.body),
              '--cms-link-font-size': lookup(BODY_TEXT_SIZES, size, BODY_TEXT_SIZES.lg),
              '--cms-link-text-decoration': resting,
              '--cms-link-hover-text-decoration': hovered,
            } as React.CSSProperties
          }
        >
          {text}
        </StyledLink>
      </Wrapper>
    );
  },
};
